import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import { type CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BrandXS,
  BrandXXS,
  HeadlineM,
  LabelL,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { CardsNumber } from '@sorare/core/src/components/collections/CardsNumber';
import { ScarcityLabel } from '@sorare/core/src/components/collections/ScarcityLabel';
import { Score } from '@sorare/core/src/components/collections/Score';
import { SplitProgressBar } from '@sorare/core/src/components/collections/SplitProgressBar';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import {
  ensureHexColor,
  getRgbColorFromHex,
  isLightColor,
} from '@sorare/core/src/lib/colors';
import { glossary } from '@sorare/core/src/lib/glossary';

import { SlotsPreview } from 'components/collections/SlotsPreview';

import { CollectionPreviewStats } from './CollectionPreviewStats';
import { EmptyCollection } from './EmptyCollection';
import { Neon } from './Neon';
import type { CollectionPreview_cardCollection } from './__generated__/index.graphql';
import inSeasonBackground from './assets/in-season.webp';

const completedRgbColors: Partial<Record<Rarity, string>> = {
  [Rarity.common]: 'var(--c-rgb-shimmer-common)',
  [Rarity.limited]: 'var(--c-rgb-shimmer-limited)',
  [Rarity.rare]: 'var(--c-rgb-shimmer-rare)',
  [Rarity.super_rare]: 'var(--c-rgb-shimmer-superRare)',
  [Rarity.unique]: 'var(--c-rgb-shimmer-unique)',
};

const shimmer = keyframes`
  0% {
    transform: translate(-70%, 70%) scale(1.2);
  }
  70% {
    transform: translate(70%, -70%) scale(1.2);
  }
  100% {
    transform: translate(70%, -70%) scale(1.2);
  }
`;

const GradientWrapper = styled.div`
  position: relative;
  isolation: isolate;
  height: 100%;
  border-radius: var(--double-unit);
  transition: transform 0.2s ease-in-out;

  &.complete {
    &::before,
    &::after {
      content: '';
      position: absolute;
      border-radius: inherit;
      inset: -1px;
      background: var(--completed-color);
      z-index: -2;
    }

    &::after {
      filter: blur(5px);
    }
  }
  &:hover {
    color: inherit;
    transform: scale(1.02);
  }
`;

const Shimmer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  border-radius: var(--double-unit);
  pointer-events: none;

  &::before {
    content: '';
    z-index: 0;
    display: block;
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to top right,
      transparent 40%,
      rgba(var(--shimmer-color), 0.15) 50%,
      transparent 60%
    );
    transform-origin: center;
    animation: ${shimmer} 3000ms cubic-bezier(0.6, 0.2, 0, 1) infinite;
  }
`;

const Root = styled(Vertical).attrs({ gap: 2 })`
  justify-content: space-between;
  height: 100%;

  border-radius: var(--double-unit);
  transition: transform 0.2s ease-in-out;

  .complete & {
    background-color: var(--c-black);
  }

  &:hover {
    color: inherit;
  }
`;

const Header = styled(Horizontal).attrs({ gap: 2, center: true })`
  position: relative;
  isolation: isolate;
  width: 100%;
  padding: var(--triple-unit) var(--double-unit);
  border-radius: var(--double-unit) var(--double-unit) 0 0;
  background:
    linear-gradient(
      0deg,
      #000 0%,
      rgba(0, 0, 0, 0.58) 71.75%,
      rgba(0, 0, 0, 0) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--collection-color-rgb), 0.4) 0%,
      rgba(var(--collection-color-rgb), 0.4) 100%
    ),
    var(--background-image) center / cover no-repeat;
`;

const FullWidthSBHorizontal = styled(SBHorizontal)`
  width: 100%;
`;

const FullWidthHorizontal = styled(Horizontal)`
  width: 100%;
`;

const Logo = styled.img`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  object-fit: contain;
`;

const WhiteColorWrapper = styled.span`
  font-weight: var(--t-bold);
  color: var(--c-white);
`;
const Completed = styled(BrandXXS)`
  position: absolute;
  top: var(--unit);
  left: var(--double-unit);
  color: var(--c-onBase-success);
`;

const HeaderRow = styled(Horizontal).attrs({ gap: 1 })`
  align-items: baseline;
`;

const SlotsWrapper = styled.div``;

const Footer = styled(Vertical).attrs({ gap: 0.5 })`
  width: 100%;
  padding: 0 var(--double-unit) var(--double-unit);
`;

const BaselineLabelL = styled(LabelL)`
  display: inline-flex;
  align-items: center;
  gap: var(--unit);
`;

type Props = {
  collection: CollectionPreview_cardCollection;
  to?: string;
  shouldBeHighlighted?: boolean;
  withStats?: boolean;
};

export const CollectionPreview = ({
  collection,
  to,
  shouldBeHighlighted,
  withStats = false,
}: Props) => {
  const {
    rarity,
    team,
    sportSeason,
    userCardCollection,
    bonusThresholds,
    snapshotted,
    inSeason,
  } = collection;
  const { slotsCount, fulfilledSlotsCount } = userCardCollection || {};
  if (!team) return null;

  const { pictureUrl, name } = team;

  const { styleGuideColor } = team;
  const { startYear, endYear, singleCivilYear } = sportSeason || {};
  const displayedStartYear = singleCivilYear
    ? startYear
    : startYear && startYear % 2000;
  const displayedEndYear = !singleCivilYear && endYear ? endYear % 2000 : null;

  const hexColor = ensureHexColor(styleGuideColor);

  const rgbColor =
    hexColor && !isLightColor(hexColor) ? getRgbColorFromHex(hexColor) : null;

  const backgroundColor =
    hexColor && !isLightColor(hexColor) ? hexColor : 'var(--c-nl-700-opaque)';

  const nextThreshold = bonusThresholds.find(({ threshold }) => {
    return threshold > (userCardCollection?.score || 0);
  })?.threshold;

  const emptyCollection = !fulfilledSlotsCount;
  const { complete } = userCardCollection || {};
  // const complete = true;
  if (emptyCollection) {
    return (
      <EmptyCollection
        rarity={rarity}
        displayedStartYear={displayedStartYear}
        displayedEndYear={displayedEndYear}
        pictureUrl={pictureUrl}
        name={name}
        snapshotted={snapshotted}
        to={to}
        inSeason={inSeason}
        backgroundColor={backgroundColor}
        rgbColor={rgbColor}
      />
    );
  }

  return (
    <GradientWrapper
      className={classNames({ complete })}
      style={
        {
          '--completed-color': hexColor,
        } as CSSProperties
      }
    >
      <Root
        style={
          {
            '--background-image': inSeason
              ? `url(${inSeasonBackground})`
              : backgroundColor,
            '--collection-color-rgb': rgbColor || 'var(--c-rgb-black)',
            '--collection-color': backgroundColor,
          } as CSSProperties
        }
        as={to ? Link : 'div'}
        to={to}
      >
        <Header>
          {!!complete && (
            <Completed color="var(--c-onBase-success)">
              <FormattedMessage {...glossary.completed} />
            </Completed>
          )}
          <FullWidthSBHorizontal>
            <FullWidthHorizontal>
              <OnboardingHighlightWrapper
                highlightName="CollectionPreviewPicture"
                wrap={!!shouldBeHighlighted}
              >
                {!!pictureUrl && <Logo alt="" src={pictureUrl} />}
              </OnboardingHighlightWrapper>
              <Vertical gap={0}>
                <HeaderRow>
                  <LabelS>
                    <ScarcityLabel scarcity={rarity || Rarity.custom_series} />
                  </LabelS>
                  <BrandXS
                    bold
                    className={classNames({
                      'text-gradient-color': inSeason,
                    })}
                    style={
                      {
                        '--text-gradient-color': `linear-gradient(
                          45deg,
                          #f8d3da,
                          #b3a9f4 28%,
                          #fbe9fb 54%,
                          #4f94fd 100%
                          )`,
                      } as CSSProperties
                    }
                  >
                    {displayedStartYear}
                    {!!displayedEndYear && `-${displayedEndYear}`}
                  </BrandXS>
                </HeaderRow>
                <OnboardingHighlightWrapper
                  highlightName="CollectionPreviewName"
                  wrap={!!shouldBeHighlighted}
                >
                  <HeadlineM>{name}</HeadlineM>
                </OnboardingHighlightWrapper>
              </Vertical>
            </FullWidthHorizontal>
            <OnboardingHighlightWrapper
              wrap={!!shouldBeHighlighted}
              highlightName="CollectionPreviewStatsCards"
            >
              <CardsNumber
                ownedCards={fulfilledSlotsCount}
                totalCards={slotsCount}
              />
            </OnboardingHighlightWrapper>
          </FullWidthSBHorizontal>
          <Neon color={backgroundColor} />
        </Header>
        <SlotsWrapper>
          <SlotsPreview
            cardCollection={collection}
            shouldBeHighlighted={shouldBeHighlighted}
          />
        </SlotsWrapper>
        <Footer>
          {!snapshotted && !!nextThreshold && (
            <SBHorizontal>
              <BaselineLabelL color="var(--c-nd-600)">
                <FormattedMessage
                  id="CollectionPreview.nextLevel"
                  defaultMessage="{score} to the next level"
                  values={{
                    score: (
                      <WhiteColorWrapper>
                        <Score
                          score={
                            nextThreshold - (userCardCollection?.score || 0)
                          }
                          rarity={rarity}
                        />
                      </WhiteColorWrapper>
                    ),
                  }}
                />
              </BaselineLabelL>
              <IconButton color="secondary" size="compact">
                <FontAwesomeIcon icon={faChevronRight} size="sm" />
              </IconButton>
            </SBHorizontal>
          )}
          <OnboardingHighlightWrapper
            highlightName="CollectionPreviewCollectionBonusProgressBar"
            wrap={!!shouldBeHighlighted}
          >
            {!!bonusThresholds.length && !snapshotted && (
              <SplitProgressBar
                cardCollection={collection}
                userCardCollection={collection.userCardCollection}
              />
            )}
          </OnboardingHighlightWrapper>
          {!!withStats && (
            <CollectionPreviewStats
              userCardCollection={userCardCollection}
              shouldBeHighlighted={shouldBeHighlighted}
              snapshotted={snapshotted}
            />
          )}
        </Footer>
        {!!complete && (
          <Shimmer
            style={
              {
                '--shimmer-color':
                  (rarity && completedRgbColors[rarity]) ||
                  'var(--c-rgb-shimmer-limited)',
              } as CSSProperties
            }
            className="visible"
          />
        )}
      </Root>
    </GradientWrapper>
  );
};

CollectionPreview.fragments = {
  cardCollection: gql`
    fragment CollectionPreview_cardCollection on CardCollection {
      slug
      rarity
      inSeason
      snapshotted
      bonusThresholds {
        threshold
      }
      team: anyTeam {
        slug
        name
        pictureUrl
        styleGuideColor
      }
      sportSeason {
        name
        startYear
        endYear
        singleCivilYear
      }
      userCardCollection(forUserSlug: $user) {
        id
        slotsCount
        fulfilledSlotsCount
        complete
        score
        ...SplitProgressBar_userCardCollection
        ...CollectionPreviewStats_userCardCollection
      }
      ...SplitProgressBar_cardCollection
      ...SlotsPreview_cardCollection
    }
    ${SplitProgressBar.fragments.cardCollection}
    ${SplitProgressBar.fragments.userCardCollection}
    ${CollectionPreviewStats.fragments.userCardCollection}
    ${SlotsPreview.fragments.cardCollection}
  ` as TypedDocumentNode<CollectionPreview_cardCollection>,
};
